import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../ui/Button';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const menuItems = [
    {
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="7" height="7" />
          <rect x="14" y="3" width="7" height="7" />
          <rect x="14" y="14" width="7" height="7" />
          <rect x="3" y="14" width="7" height="7" />
        </svg>
      ),
      label: 'Dashboard',
      path: '/dashboard',
      description: 'View your projects'
    },
    {
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      ),
      label: 'Analysis',
      path: '/analysis',
      description: 'Analyze properties'
    },
    {
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="12" y1="1" x2="12" y2="23" />
          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H17" />
        </svg>
      ),
      label: 'Pricing',
      path: '/pricing',
      description: 'Buy credits'
    },
    {
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      ),
      label: 'Profile',
      path: '/profile',
      description: 'Account settings'
    }
  ];

  const isActive = (path: string) => {
    if (path === '/dashboard' && location.pathname === '/') return true;
    return location.pathname === path;
  };

  return (
    <>
      {/* Mobile overlay */}
      <div
        className={`sidebar-overlay ${isOpen ? 'active' : ''}`}
        onClick={onToggle}
        aria-hidden="true"
      />

      {/* Sidebar */}
      <aside
        className={`sidebar ${isOpen ? 'open' : ''}`}
        role="navigation"
        aria-label="Main navigation"
      >
        {/* Logo */}
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <div className="sidebar-logo" aria-hidden="true">
              M
            </div>
            <div className="sidebar-brand-text">
              <h1>MavScope</h1>
              <p>AI Measurements</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav" aria-label="Primary">
          <ul className="sidebar-menu">
            {menuItems.map((item) => (
              <li key={item.path} className="sidebar-menu-item">
                <button
                  onClick={() => {
                    navigate(item.path);
                    if (window.innerWidth < 768) {
                      onToggle();
                    }
                  }}
                  className={`sidebar-menu-button ${isActive(item.path) ? 'active' : ''}`}
                  aria-label={`${item.label}: ${item.description}`}
                  aria-current={isActive(item.path) ? 'page' : undefined}
                >
                  <span className="sidebar-menu-icon" aria-hidden="true">{item.icon}</span>
                  <div className="sidebar-menu-content">
                    <span className="sidebar-menu-label">{item.label}</span>
                    <span className="sidebar-menu-description">{item.description}</span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* User section */}
        <div className="sidebar-footer">
          <div className="sidebar-user-card">
            <div className="sidebar-user-info">
              <div className="sidebar-user-avatar" aria-hidden="true">
                {user?.email?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="sidebar-user-details">
                <div className="sidebar-user-email" title={user?.email || 'Guest'}>
                  {user?.email || 'Guest'}
                </div>
                <div className="sidebar-user-credits">
                  {user?.credits || 0} credits
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              fullWidth
              onClick={() => {
                navigate('/pricing');
                if (window.innerWidth < 768) {
                  onToggle();
                }
              }}
              style={{ marginBottom: 'var(--spacing-2)' }}
            >
              Buy Credits
            </Button>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            fullWidth
            onClick={handleLogout}
            isLoading={isLoggingOut}
            style={{ color: 'var(--color-gray-600)' }}
          >
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Mobile toggle button */}
      <button
        onClick={onToggle}
        className="sidebar-toggle"
        aria-label={isOpen ? 'Close navigation menu' : 'Open navigation menu'}
        aria-expanded={isOpen}
        aria-controls="sidebar-navigation"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      </button>
    </>
  );
};

export default Sidebar;
